import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FormControl, Select, MenuItem, Button } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ProductSelector from './ProductSelector';
import MotivationPostsDisplay from './MotivationPostsDisplay';
import NPSChart from './NPSChart';
import { prepareChartData } from '../functions/bayesianNPS';
import { apiPost } from '../services/apiClient';

const DEMOGRAPHIC_COLORS = [
    '#6366F1', // indigo
    '#14B8A6', // teal
    '#F59E0B', // amber
    '#EC4899', // pink
    '#8B5CF6', // violet
    '#06B6D4', // cyan
    '#F97316', // orange
];

const UNKNOWN_COLOR = '#E2E8F0';
const UNKNOWN_STROKE = '#CBD5E1';

const TIME_RANGE_OPTIONS = [
    { value: 3, label: '3 Months' },
    { value: 6, label: '6 Months' },
    { value: 12, label: '12 Months' },
    { value: 24, label: '24 Months' },
    { value: 36, label: '36 Months' },
    { value: 60, label: '60 Months' },
];

const SHORT_ACRONYMS = new Set(['us', 'uk', 'eu', 'apac', 'emea', 'latam', 'mena']);

const formatTypeName = (type) => {
    return type.split('_')
        .map(word => SHORT_ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// Compute rolling average shares from daily timeseries data
// rollingDays: size of the rolling window
const computeRollingShares = (timeseries, allValues, rollingDays) => {
    if (!timeseries || timeseries.length === 0) return [];

    return timeseries.map((point, index) => {
        // Collect counts from the rolling window
        const windowStart = Math.max(0, index - rollingDays + 1);
        const rollingCounts = {};
        allValues.forEach(v => { rollingCounts[v] = 0; });
        let rollingTotal = 0;

        for (let i = windowStart; i <= index; i++) {
            const p = timeseries[i];
            allValues.forEach(v => {
                const count = p.counts?.[v] || 0;
                rollingCounts[v] += count;
                rollingTotal += count;
            });
        }

        // Convert to percentages
        const rollingShares = {};
        allValues.forEach(v => {
            rollingShares[v] = rollingTotal > 0 ? (rollingCounts[v] / rollingTotal) * 100 : 0;
        });

        return {
            timestamp: new Date(point.bucket_start).getTime(),
            totalPosts: point.total_posts || 0,
            ...rollingShares,
        };
    });
};

// Fill gaps in daily timeseries (carry forward last known data)
const fillTimeseriesGaps = (timeseries) => {
    if (!timeseries || timeseries.length <= 1) return timeseries;

    const sorted = [...timeseries].sort((a, b) =>
        new Date(a.bucket_start) - new Date(b.bucket_start)
    );

    const toDateKey = (d) => {
        const date = new Date(d);
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    };

    const dataByDate = new Map();
    for (const point of sorted) {
        const key = toDateKey(point.bucket_start);
        const existing = dataByDate.get(key);
        if (!existing || (point.total_posts || 0) > (existing.total_posts || 0)) {
            dataByDate.set(key, point);
        }
    }

    const startDate = new Date(sorted[0].bucket_start);
    const endDate = new Date(sorted[sorted.length - 1].bucket_start);
    const filled = [];
    let lastGoodData = null;

    for (let d = new Date(startDate); d <= endDate; d.setUTCDate(d.getUTCDate() + 1)) {
        const dateKey = toDateKey(d);
        const existingData = dataByDate.get(dateKey);

        if (existingData) {
            if ((existingData.total_posts || 0) > 0) {
                lastGoodData = existingData;
            }
            filled.push(existingData);
        } else {
            // Missing day — insert empty bucket
            filled.push({
                bucket_start: `${dateKey}T00:00:00.000Z`,
                total_posts: 0,
                counts: lastGoodData ? { ...Object.fromEntries(Object.keys(lastGoodData.counts || {}).map(k => [k, 0])) } : {},
            });
        }
    }

    return filled;
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const date = new Date(label);
    const totalPosts = payload[0]?.payload?.totalPosts || 0;

    const entries = payload
        .filter(entry => entry.dataKey !== 'totalPosts')
        .sort((a, b) => (b.value || 0) - (a.value || 0));

    return (
        <div style={{
            backgroundColor: '#1E293B',
            padding: '12px 14px',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            minWidth: '170px',
            border: 'none',
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: '8px',
            }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#F1F5F9' }}>
                    {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
            </div>
            <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '8px' }}>
                {totalPosts.toLocaleString()} posts (rolling window)
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {entries.map((entry, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '2px',
                                backgroundColor: entry.color,
                                flexShrink: 0,
                            }} />
                            <span style={{ color: '#CBD5E1' }}>{entry.name}</span>
                        </div>
                        <span style={{
                            fontWeight: 700,
                            color: '#F1F5F9',
                            fontVariantNumeric: 'tabular-nums',
                            marginLeft: '16px',
                        }}>
                            {(entry.value || 0).toFixed(1)}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DemographicEvaluator = ({ topicMap, companyName,
    // Optional shared product selection: adopted into the pending (not
    // applied) product dropdown when valid here; applied selections here
    // are pushed back up so other sections of the page can pick them up.
    selectedProduct: sharedSelectedProduct, onProductChange }) => {
    // Filter state
    // Lazy-initialize from the shared selection (if valid) so local state
    // is never empty on first render — see PricingPlot.js for why an
    // empty initial value lets ProductSelector's own internal auto-select
    // effect win a race and pre-fill the pending dropdown with its own
    // default instead of the incoming shared topic.
    const [selectedProduct, setSelectedProduct] = useState(() =>
        (sharedSelectedProduct && topicMap?.[sharedSelectedProduct]) ? sharedSelectedProduct : null
    );
    const [appliedProduct, setAppliedProduct] = useState(null);
    const [selectedType, setSelectedType] = useState('');
    const [appliedType, setAppliedType] = useState('');
    const [timeRange, setTimeRange] = useState(12);
    const [appliedTimeRange, setAppliedTimeRange] = useState(12);

    // View mode: 'breakdown' (% of mentions) or 'nps' (NPS comparison)
    const [viewMode, setViewMode] = useState('breakdown');

    // Data state
    const [chartData, setChartData] = useState([]);
    const [demographicValues, setDemographicValues] = useState([]);
    const [availableTypes, setAvailableTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [summaryMetrics, setSummaryMetrics] = useState(null);

    // Posts display state
    const [postsReferenced, setPostsReferenced] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [postsError, setPostsError] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    // Data range
    const [dataRange, setDataRange] = useState({ start: null, end: null });

    // NPS by segment state
    const [npsBySegment, setNpsBySegment] = useState(null);
    const [npsLoading, setNpsLoading] = useState(false);
    const [showNpsConfidence, setShowNpsConfidence] = useState(false);

    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};
        const filtered = {};
        Object.keys(topicMap).forEach(id => {
            if (topicMap[id]) filtered[id] = topicMap[id];
        });
        filtered['all'] = { id: 'all', name: 'All Topics', priority: 9999 };
        return filtered;
    }, [topicMap]);

    // Pre-select the page-wide shared topic in the (pending) dropdown when
    // valid here. Doesn't auto-apply — matches this component's existing
    // "select then click Apply" behavior.
    useEffect(() => {
        if (sharedSelectedProduct && filteredTopicMap[sharedSelectedProduct] && sharedSelectedProduct !== selectedProduct) {
            setSelectedProduct(sharedSelectedProduct);
        }
    }, [sharedSelectedProduct, filteredTopicMap]);

    const handleProductSelect = useCallback((id) => {
        setSelectedProduct(id);
    }, []);

    // Fetch available types when product changes
    useEffect(() => {
        const fetchTypes = async () => {
            if (!selectedProduct) return;

            let topicIdToFetch;
            if (selectedProduct !== 'all') {
                topicIdToFetch = selectedProduct;
            } else {
                const actualTopics = Object.values(topicMap).filter(t => t.id !== 'all');
                if (actualTopics.length === 0) return;
                const sorted = actualTopics.sort((a, b) =>
                    (a.priority ?? Infinity) - (b.priority ?? Infinity)
                );
                topicIdToFetch = sorted[0].id;
            }

            try {
                const response = await apiPost('topic/demographics/types', { topic_id: topicIdToFetch });
                if (!response.ok) throw new Error(`Backend error: ${response.status}`);
                const json = await response.json();
                const types = (json.data ?? json ?? []).map(t => ({
                    value: typeof t === 'string' ? t : t.type,
                    label: formatTypeName(typeof t === 'string' ? t : t.type),
                }));
                setAvailableTypes(types);
                if (types.length > 0 && !types.some(t => t.value === selectedType)) {
                    setSelectedType(types[0].value);
                }
            } catch (err) {
                console.error('Error fetching demographic types:', err);
            }
        };

        fetchTypes();
    }, [selectedProduct, topicMap]);

    const isFiltersDirty = useMemo(() => {
        if (!appliedProduct && !selectedProduct) return false;
        if (selectedProduct !== appliedProduct) return true;
        if (selectedType !== appliedType) return true;
        if (timeRange !== appliedTimeRange) return true;
        return false;
    }, [selectedProduct, appliedProduct, selectedType, appliedType, timeRange, appliedTimeRange]);

    // Fetch and process data
    const fetchData = useCallback(async (productId, type, months) => {
        setIsLoading(true);
        setError(null);
        setPostsReferenced([]);
        setSelectedPeriod(null);

        try {
            const endDate = new Date();
            endDate.setHours(23, 59, 59, 999);
            const startDate = new Date(endDate);
            startDate.setMonth(endDate.getMonth() - months);

            // Fetch extra data before display start for rolling average warm-up
            const dataStartDate = new Date(startDate);
            dataStartDate.setFullYear(dataStartDate.getFullYear() - 1);

            const topicIds = productId === 'all'
                ? Object.keys(topicMap).filter(id => id !== 'all')
                : [productId];

            // Fetch data for all relevant topics
            const allData = await Promise.all(
                topicIds.map(async (id) => {
                    try {
                        const response = await apiPost('topic/demographics/overview', {
                            topic_id: id,
                            start_date: dataStartDate.toISOString(),
                            end_date: endDate.toISOString(),
                            demographic_type: type,
                        });
                        if (!response.ok) throw new Error(`HTTP ${response.status}`);
                        const json = await response.json();
                        return json.data ?? json ?? null;
                    } catch (err) {
                        console.error(`Error fetching demographics for topic ${id}:`, err);
                        return null;
                    }
                })
            );

            const validData = allData.filter(d => d && d.timeseries && d.timeseries.length > 0);

            if (validData.length === 0) {
                setChartData([]);
                setDemographicValues([]);
                setSummaryMetrics(null);
                setDataRange({ start: null, end: null });
                return;
            }

            // Aggregate timeseries across topics if "All Topics"
            let timeseries;
            if (validData.length === 1) {
                timeseries = validData[0].timeseries;
            } else {
                // Merge by date
                const dateMap = {};
                validData.forEach(d => {
                    d.timeseries.forEach(point => {
                        const key = point.bucket_start.split('T')[0];
                        if (!dateMap[key]) {
                            dateMap[key] = { bucket_start: point.bucket_start, total_posts: 0, counts: {} };
                        }
                        dateMap[key].total_posts += point.total_posts || 0;
                        Object.entries(point.counts || {}).forEach(([val, count]) => {
                            dateMap[key].counts[val] = (dateMap[key].counts[val] || 0) + count;
                        });
                    });
                });
                timeseries = Object.values(dateMap).sort((a, b) =>
                    new Date(a.bucket_start) - new Date(b.bucket_start)
                );
            }

            // Fill gaps
            timeseries = fillTimeseriesGaps(timeseries);

            // Discover all demographic values
            const allValues = new Set();
            timeseries.forEach(p => {
                Object.keys(p.counts || {}).forEach(v => allValues.add(v));
            });
            const sortedValues = Array.from(allValues).sort((a, b) => {
                const aUnk = a.toLowerCase() === 'unknown' || a.toLowerCase() === 'other';
                const bUnk = b.toLowerCase() === 'unknown' || b.toLowerCase() === 'other';
                if (aUnk && !bUnk) return 1;
                if (!aUnk && bUnk) return -1;
                return a.localeCompare(b);
            });

            // Compute rolling averages
            const rollingDays = months <= 3 ? 30 : months <= 12 ? 90 : 365;
            const rollingData = computeRollingShares(timeseries, sortedValues, rollingDays);

            // Filter to display range
            const displayStartTimestamp = startDate.getTime();
            const displayEndTimestamp = endDate.getTime();
            const displayData = rollingData.filter(p =>
                p.timestamp >= displayStartTimestamp && p.timestamp <= displayEndTimestamp
            );

            // Total posts: sum from the display-range daily buckets
            const totalPosts = displayData.reduce((sum, p) => sum + (p.totalPosts || 0), 0);

            setDemographicValues(sortedValues);
            setChartData(displayData);
            setSummaryMetrics({ totalPosts });

            if (displayData.length > 0) {
                setDataRange({
                    start: new Date(displayData[0].timestamp),
                    end: new Date(displayData[displayData.length - 1].timestamp),
                });
            }

            // Fetch NPS by demographic segment (single topic only)
            if (productId !== 'all') {
                setNpsLoading(true);
                try {
                    const npsResponse = await apiPost('topic/demographics/nps', {
                        topic_id: productId,
                        demographic_type: type,
                        months_back: months,
                    });
                    if (npsResponse.ok) {
                        const npsJson = await npsResponse.json();
                        setNpsBySegment(npsJson.data ?? null);
                    } else {
                        console.error('NPS by segment endpoint error:', npsResponse.status);
                        setNpsBySegment(null);
                    }
                } catch (npsErr) {
                    console.error('Error fetching NPS by segment:', npsErr);
                    setNpsBySegment(null);
                } finally {
                    setNpsLoading(false);
                }
            } else {
                setNpsBySegment(null);
            }

        } catch (err) {
            console.error('Error processing demographics data:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [topicMap]);

    const applyFilters = () => {
        setAppliedProduct(selectedProduct);
        setAppliedType(selectedType);
        setAppliedTimeRange(timeRange);
        fetchData(selectedProduct, selectedType, timeRange);
        if (onProductChange && selectedProduct && selectedProduct !== 'all') onProductChange(selectedProduct);
    };

    // Handle chart click to fetch posts
    const handleChartClick = useCallback(async (e) => {
        console.log('Demographics chart clicked:', e);
        if (!e || !e.activePayload || e.activePayload.length === 0) {
            console.log('No active payload in click event');
            return;
        }
        if (!appliedProduct || appliedProduct === 'all') {
            console.log('Skipping click: product is', appliedProduct);
            return;
        }

        const pointData = e.activePayload[0].payload;
        const clickedTimestamp = pointData.timestamp;

        const endDate = new Date(clickedTimestamp);
        const startDate = new Date(clickedTimestamp);
        startDate.setDate(startDate.getDate() - 30);

        const periodLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} \u2013 ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

        setPostsLoading(true);
        setPostsError(null);
        setPostsReferenced([]);
        setSelectedPeriod(periodLabel);

        try {
            const response = await apiPost('topic/demographics/posts', {
                topic_id: appliedProduct,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                demographic_type: appliedType,
            });

            if (!response.ok) throw new Error(`Failed to fetch posts: ${response.status}`);

            const json = await response.json();
            const rawPosts = json.data ?? json ?? [];
            // Normalize posts for MotivationPost:
            // - demographic_value → value (tag display)
            // - time: epoch seconds → ISO string (date display)
            // - strip rating (no confidence for demographics)
            // - map explanation if present
            const posts = (Array.isArray(rawPosts) ? rawPosts : []).map(p => ({
                ...p,
                value: p.value || p.demographic_value || appliedType,
                time: p.time && p.time < 1e12
                    ? new Date(p.time * 1000).toISOString()  // epoch seconds → ISO
                    : p.time,                                 // already ms or ISO string
                rating: p.rating ?? p.confidence ?? undefined,
                explanation: p.explanation || p.demographic_explanation || null,
            }));
            setPostsReferenced(posts);

            if (Array.isArray(posts) && posts.length === 0) {
                setPostsError('No posts found for this period.');
            }
        } catch (err) {
            console.error('Error fetching demographics posts:', err);
            setPostsError(`Could not load posts: ${err.message}`);
            setPostsReferenced([]);
        } finally {
            setPostsLoading(false);
        }
    }, [appliedProduct, appliedType]);

    const handleClosePosts = useCallback(() => {
        setPostsReferenced([]);
        setPostsError(null);
        setSelectedPeriod(null);
    }, []);

    // Assign colors
    const valueColors = useMemo(() => {
        const colors = {};
        let colorIndex = 0;
        demographicValues.forEach(value => {
            const lower = value.toLowerCase();
            if (lower === 'unknown' || lower === 'other') {
                colors[value] = UNKNOWN_COLOR;
            } else {
                colors[value] = DEMOGRAPHIC_COLORS[colorIndex % DEMOGRAPHIC_COLORS.length];
                colorIndex++;
            }
        });
        return colors;
    }, [demographicValues]);

    // Trend computation
    const trendData = useMemo(() => {
        if (chartData.length < 2) return null;
        const first = chartData[0];
        const last = chartData[chartData.length - 1];
        const trends = {};
        demographicValues.forEach(v => {
            trends[v] = (last[v] || 0) - (first[v] || 0);
        });
        return trends;
    }, [chartData, demographicValues]);

    // Current summary from latest chart point
    const currentSummary = useMemo(() => {
        if (chartData.length === 0) return null;
        const latest = chartData[chartData.length - 1];
        return {
            timestamp: latest.timestamp,
            values: demographicValues.map(v => ({
                key: v,
                name: formatTypeName(v),
                percent: latest[v] || 0,
                color: valueColors[v],
                trend: trendData?.[v] || 0,
                isUnknown: v.toLowerCase() === 'unknown' || v.toLowerCase() === 'other',
            })),
        };
    }, [chartData, demographicValues, valueColors, trendData]);

    const hasData = chartData.length > 0;
    const knownValues = currentSummary?.values.filter(v => !v.isUnknown) || [];
    const unknownValues = currentSummary?.values.filter(v => v.isUnknown) || [];

    // NPS by segment chart data
    const NPS_SEGMENT_COLORS = [
        { main: '#6366F1' },  // indigo
        { main: '#14B8A6' },  // teal
        { main: '#F59E0B' },  // amber
        { main: '#EC4899' },  // pink
        { main: '#8B5CF6' },  // violet
        { main: '#06B6D4' },  // cyan
    ];

    const { npsChartData, npsValueByDataset } = useMemo(() => {
        if (!npsBySegment) return { npsChartData: null, npsValueByDataset: [] };

        const entries = Object.entries(npsBySegment).filter(([key]) => {
            const lower = key.toLowerCase();
            return lower !== 'unknown' && lower !== 'other';
        });

        if (entries.length === 0) return { npsChartData: null, npsValueByDataset: [] };

        // Collect all months across all segments
        const allMonths = new Set();
        entries.forEach(([, results]) => {
            (results || []).forEach(r => allMonths.add(r.month));
        });
        const sortedMonths = Array.from(allMonths).sort();

        if (sortedMonths.length === 0) return { npsChartData: null, npsValueByDataset: [] };

        // Build datasets — one line per demographic value
        const allDatasets = [];
        const valueByDataset = [];  // datasetIndex → raw demographic value
        entries.forEach(([value, results], i) => {
            const transformed = (results || []).map(r => ({
                month: r.month,
                meanNPS: r.nps,
                lowerCI: r.lower_ci,
                upperCI: r.upper_ci,
                count: r.sample_size,
                isForwardFilled: r.is_forward_filled || false,
            }));
            const color = NPS_SEGMENT_COLORS[i % NPS_SEGMENT_COLORS.length];
            const prepared = prepareChartData(transformed, formatTypeName(value), showNpsConfidence, color);
            if (prepared) {
                prepared.datasets.forEach(() => valueByDataset.push(value));
                allDatasets.push(...prepared.datasets);
            }
        });

        return {
            npsChartData: { labels: sortedMonths, datasets: allDatasets },
            npsValueByDataset: valueByDataset,
        };
    }, [npsBySegment, showNpsConfidence]);

    // NPS audit click — fetch posts for a (demographic_value, month) bucket
    const handleNpsPointClick = useCallback(async ({ datasetIndex, label }) => {
        if (!appliedProduct || appliedProduct === 'all') return;
        if (!label || datasetIndex == null) return;

        const rawValue = npsValueByDataset[datasetIndex];
        if (!rawValue) return;

        // label is the month string from the chart (e.g. "2026-01" or "Jan 2026").
        // Parse defensively — accept ISO YYYY-MM, YYYY-MM-DD, or any string Date can read.
        let monthStart;
        const isoMonthMatch = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(String(label));
        if (isoMonthMatch) {
            monthStart = new Date(Date.UTC(Number(isoMonthMatch[1]), Number(isoMonthMatch[2]) - 1, 1));
        } else {
            const parsed = new Date(label);
            if (isNaN(parsed.getTime())) return;
            monthStart = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1));
        }
        const monthEnd = new Date(Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth() + 1, 1));
        monthEnd.setUTCMilliseconds(monthEnd.getUTCMilliseconds() - 1);

        const monthLabel = monthStart.toLocaleDateString('en-US', {
            month: 'long', year: 'numeric', timeZone: 'UTC',
        });
        const periodLabel = `${formatTypeName(rawValue)} • ${monthLabel}`;

        setPostsLoading(true);
        setPostsError(null);
        setPostsReferenced([]);
        setSelectedPeriod(periodLabel);

        try {
            const response = await apiPost('topic/demographics/nps-posts', {
                topic_id: appliedProduct,
                demographic_type: appliedType,
                demographic_value: rawValue,
                start_date: monthStart.toISOString(),
                end_date: monthEnd.toISOString(),
            });

            if (!response.ok) throw new Error(`Failed to fetch posts: ${response.status}`);

            const json = await response.json();
            const rawPosts = json.data ?? json ?? [];
            const posts = (Array.isArray(rawPosts) ? rawPosts : []).map(p => ({
                ...p,
                value: p.value || p.demographic_value || rawValue,
                time: p.time && p.time < 1e12
                    ? new Date(p.time * 1000).toISOString()
                    : p.time,
                rating: p.rating ?? p.confidence ?? undefined,
                explanation: p.explanation || p.demographic_explanation || null,
            }));
            setPostsReferenced(posts);

            if (posts.length === 0) {
                setPostsError('No posts found for this segment and month.');
            }
        } catch (err) {
            console.error('Error fetching NPS demographic posts:', err);
            setPostsError(`Could not load posts: ${err.message}`);
            setPostsReferenced([]);
        } finally {
            setPostsLoading(false);
        }
    }, [appliedProduct, appliedType, npsValueByDataset]);

    // Consistent control styles
    const CONTROL_HEIGHT = 36;
    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px', paddingRight: '24px !important' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0', transition: 'border-color 0.15s ease' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366F1' },
    };
    const filterLabelStyle = {
        fontSize: '9px', fontWeight: 600, color: '#999',
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px',
    };

    return (
        <div style={{ padding: '24px', maxWidth: '100%', boxSizing: 'border-box' }}>
            <style>{`
                @keyframes pulseRing {
                    0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); }
                    70% { box-shadow: 0 0 0 6px rgba(99,102,241,0); }
                    100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>

            {/* Section Label */}
            <div style={{
                fontSize: '10px', fontWeight: 600, color: '#888',
                textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px',
            }}>
                DEMOGRAPHIC EVALUATOR
            </div>

            {/* Header Card */}
            <div style={{
                backgroundColor: appliedProduct ? '#fafafa' : '#F5F3FF',
                borderRadius: '8px',
                padding: '20px 24px 18px',
                marginBottom: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
            }}>
                {/* Title */}
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#777', fontWeight: 500, lineHeight: 1.2, marginBottom: '1px' }}>
                        {companyName || 'Company'}
                    </div>
                    {appliedProduct ? (
                        <>
                            <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0, color: '#111', lineHeight: 1.25 }}>
                                {filteredTopicMap[appliedProduct]?.name || 'Product'} &middot; {formatTypeName(appliedType)}
                            </h1>
                            <div style={{ fontSize: '12px', color: '#999', lineHeight: 1.3, marginTop: '2px' }}>
                                {TIME_RANGE_OPTIONS.find(o => o.value === appliedTimeRange)?.label || `${appliedTimeRange} Months`}
                                {appliedProduct !== 'all' && ' \u2022 Click chart to view posts'}
                            </div>
                        </>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0' }}>
                            <span style={{ fontSize: '15px', color: '#8896b3', fontWeight: 500 }}>
                                Select a product and click Apply to view demographic data
                            </span>
                        </div>
                    )}
                </div>

                {/* Filter Bar */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', rowGap: '12px' }}>
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 180 }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={handleProductSelect}
                            label=""
                            size="small"
                            sx={selectStyle}
                        />
                    </div>

                    {availableTypes.length > 0 && (
                        <div style={{ flex: '1 1 auto', minWidth: 120, maxWidth: 200 }}>
                            <div style={filterLabelStyle}>Demographic Type</div>
                            <FormControl size="small" sx={selectStyle}>
                                <Select
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    displayEmpty
                                >
                                    {availableTypes.map(t => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    )}

                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 140 }}>
                        <div style={filterLabelStyle}>Time Range</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                            >
                                {TIME_RANGE_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div style={{ flex: '0 0 auto' }}>
                        <div style={{ ...filterLabelStyle, visibility: 'hidden' }}>&nbsp;</div>
                        <Button
                            variant="contained"
                            onClick={applyFilters}
                            disabled={!selectedProduct}
                            sx={{
                                height: CONTROL_HEIGHT,
                                minWidth: isFiltersDirty ? '120px' : '72px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                textTransform: 'none',
                                backgroundColor: isFiltersDirty ? '#6366F1' : '#6366F1',
                                boxShadow: 'none',
                                animation: isFiltersDirty ? 'pulseRing 1.5s ease-out infinite' : 'none',
                                transition: 'background-color 0.15s ease, box-shadow 0.15s ease, min-width 0.2s ease',
                                '&:hover': { boxShadow: '0 2px 8px rgba(99,102,241,0.4)', backgroundColor: '#4F46E5' },
                                '&.Mui-disabled': { backgroundColor: '#C7D2FE', color: '#fff' },
                            }}
                        >
                            {isFiltersDirty ? 'Apply Changes' : 'Apply'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* View Mode Toggle */}
            {appliedProduct && !isLoading && !error && hasData && (
                <div style={{
                    display: 'inline-flex',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    overflow: 'hidden',
                    backgroundColor: '#F8FAFC',
                    marginBottom: '20px',
                }}>
                    {[
                        { value: 'breakdown', label: '% of Mentions' },
                        { value: 'nps', label: 'NPS Comparison' },
                    ].map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => setViewMode(opt.value)}
                            style={{
                                padding: '8px 20px',
                                fontSize: '13px',
                                fontWeight: viewMode === opt.value ? 600 : 400,
                                color: viewMode === opt.value ? '#fff' : '#64748B',
                                backgroundColor: viewMode === opt.value ? '#6366F1' : 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Loading State */}
            {isLoading && (
                <div>
                    {(() => {
                        const shimmerStyle = {
                            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 1.5s ease-in-out infinite',
                            borderRadius: '8px',
                        };
                        return (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                                    {[0, 1, 2].map(i => <div key={i} style={{ ...shimmerStyle, height: '100px' }} />)}
                                </div>
                                <div style={{ ...shimmerStyle, width: '100%', height: '380px' }} />
                            </>
                        );
                    })()}
                </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
                <div style={{
                    padding: '20px 24px',
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fecaca',
                    borderLeft: '4px solid #ef4444',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
                            Error loading data
                        </div>
                        <div style={{ fontSize: '13px', color: '#b91c1c', marginBottom: '12px' }}>{error}</div>
                        <button
                            onClick={() => fetchData(appliedProduct, appliedType, appliedTimeRange)}
                            style={{
                                padding: '6px 16px', fontSize: '13px', fontWeight: 500,
                                color: '#991b1b', backgroundColor: '#fee2e2', border: '1px solid #fecaca',
                                borderRadius: '6px', cursor: 'pointer',
                            }}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            )}

            {/* Main Content — Breakdown View */}
            {!isLoading && !error && hasData && viewMode === 'breakdown' && (
                <>
                    {/* Summary Cards */}
                    {currentSummary && knownValues.length > 0 && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${Math.min(knownValues.length, 4)}, 1fr)`,
                            gap: '12px',
                            marginBottom: '20px',
                            animation: 'fadeInUp 0.4s ease both',
                        }}>
                            {knownValues
                                .sort((a, b) => b.percent - a.percent)
                                .map((v) => (
                                    <div key={v.key} style={{
                                        padding: '16px 18px',
                                        borderRadius: '10px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #F1F5F9',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: v.color }} />
                                            <span style={{
                                                fontSize: '12px', fontWeight: 500, color: '#64748B',
                                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                            }}>
                                                {v.name}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                            <span style={{
                                                fontSize: '28px', fontWeight: 700, color: '#1E293B',
                                                lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                                            }}>
                                                {v.percent.toFixed(1)}
                                            </span>
                                            <span style={{ fontSize: '14px', fontWeight: 500, color: '#94A3B8' }}>%</span>
                                            {trendData && Math.abs(v.trend) >= 0.5 && (
                                                <span style={{
                                                    fontSize: '11px', fontWeight: 600,
                                                    color: v.trend > 0 ? '#10B981' : '#EF4444',
                                                    backgroundColor: v.trend > 0 ? '#ECFDF5' : '#FEF2F2',
                                                    padding: '2px 6px', borderRadius: '4px', marginLeft: '2px',
                                                }}>
                                                    {v.trend > 0 ? '+' : ''}{v.trend.toFixed(1)}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{
                                            marginTop: '10px', height: '4px', backgroundColor: '#F1F5F9',
                                            borderRadius: '2px', overflow: 'hidden',
                                        }}>
                                            <div style={{
                                                height: '100%', width: `${Math.min(v.percent, 100)}%`,
                                                backgroundColor: v.color, borderRadius: '2px',
                                                transition: 'width 0.5s ease',
                                            }} />
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    )}

                    {/* Data coverage bar */}
                    {dataRange.start && dataRange.end && (
                        <div style={{ marginBottom: '12px', animation: 'fadeInUp 0.4s ease both 30ms' }}>
                            <div style={{
                                display: 'flex', height: '8px', borderRadius: '4px',
                                overflow: 'hidden', backgroundColor: '#F1F5F9',
                            }}>
                                {(currentSummary?.values || [])
                                    .sort((a, b) => b.percent - a.percent)
                                    .map(v => (
                                        <div key={v.key} style={{
                                            width: `${v.percent}%`, backgroundColor: v.color,
                                            transition: 'width 0.5s ease',
                                        }} />
                                    ))
                                }
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                                <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                                    {dataRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    {' \u2014 '}
                                    {dataRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                                <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                                    {summaryMetrics?.totalPosts?.toLocaleString() || 0} posts
                                    {unknownValues.length > 0 && ` \u00B7 ${unknownValues[0].percent.toFixed(0)}% unclassified`}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Chart */}
                    <div style={{
                        width: '100%', height: 420,
                        backgroundColor: '#FAFBFD', borderRadius: '12px',
                        border: '1px solid #F1F5F9',
                        padding: '16px 16px 12px 16px',
                        boxSizing: 'border-box',
                        animation: 'fadeInUp 0.4s ease both 60ms',
                        cursor: appliedProduct !== 'all' ? 'pointer' : 'default',
                    }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={chartData}
                                margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                                onClick={appliedProduct !== 'all' ? handleChartClick : undefined}
                            >
                                <defs>
                                    {demographicValues.map((value) => {
                                        const color = valueColors[value];
                                        const isUnknown = value.toLowerCase() === 'unknown' || value.toLowerCase() === 'other';
                                        return (
                                            <linearGradient key={value} id={`grad-${value}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={color} stopOpacity={isUnknown ? 0.3 : 0.8} />
                                                <stop offset="100%" stopColor={color} stopOpacity={isUnknown ? 0.15 : 0.6} />
                                            </linearGradient>
                                        );
                                    })}
                                </defs>
                                <CartesianGrid strokeDasharray="none" stroke="#F1F5F9" vertical={false} />
                                <XAxis
                                    dataKey="timestamp"
                                    tickFormatter={(ts) => {
                                        const date = new Date(ts);
                                        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                                    }}
                                    tick={{ fontSize: 11, fill: '#94A3B8' }}
                                    axisLine={{ stroke: '#E2E8F0' }}
                                    tickLine={false}
                                    dy={8}
                                    ticks={chartData.reduce((acc, point, i) => {
                                        const totalPoints = chartData.length;
                                        const numTicks = appliedTimeRange >= 24 ? 8 : 6;
                                        const step = Math.floor(totalPoints / (numTicks - 1));
                                        if (i % step === 0) acc.push(point.timestamp);
                                        if (i === totalPoints - 1 && acc.length > 0) {
                                            const lastIdx = chartData.findIndex(p => p.timestamp === acc[acc.length - 1]);
                                            if (i - lastIdx >= step / 2) acc.push(point.timestamp);
                                        }
                                        return acc;
                                    }, [])}
                                />
                                <YAxis
                                    domain={[0, 100]}
                                    tickFormatter={(value) => `${value}%`}
                                    ticks={[0, 25, 50, 75, 100]}
                                    tick={{ fontSize: 11, fill: '#94A3B8' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    content={<CustomTooltip />}
                                    cursor={{ stroke: '#6366F1', strokeWidth: 1, strokeDasharray: '4 4' }}
                                />
                                {demographicValues.map((value) => {
                                    const isUnknown = value.toLowerCase() === 'unknown' || value.toLowerCase() === 'other';
                                    return (
                                        <Area
                                            key={value}
                                            type="monotone"
                                            dataKey={value}
                                            name={formatTypeName(value)}
                                            stackId="1"
                                            stroke={isUnknown ? UNKNOWN_STROKE : valueColors[value]}
                                            fill={`url(#grad-${value})`}
                                            strokeWidth={isUnknown ? 0.5 : 1.5}
                                            isAnimationActive={false}
                                        />
                                    );
                                })}
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Insights strip */}
                    {summaryMetrics && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${Math.min(knownValues.length + 1, 4)}, 1fr)`,
                            gap: '16px',
                            marginTop: '20px',
                            animation: 'fadeInUp 0.4s ease both 90ms',
                        }}>
                            {/* Total Posts card */}
                            <div style={{
                                padding: '20px',
                                background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                                borderRadius: '12px',
                                borderBottom: '3px solid #6366F1',
                            }}>
                                <div style={{ fontSize: '32px', fontWeight: 700, color: '#6366F1' }}>
                                    {summaryMetrics.totalPosts >= 1000
                                        ? `${(summaryMetrics.totalPosts / 1000).toFixed(1)}k`
                                        : summaryMetrics.totalPosts.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: '#555', fontWeight: 500, marginTop: '4px' }}>
                                    Posts Analyzed
                                </div>
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                    {TIME_RANGE_OPTIONS.find(o => o.value === appliedTimeRange)?.label || `${appliedTimeRange} Months`}
                                </div>
                            </div>

                            {/* Value-specific insight cards */}
                            {knownValues
                                .sort((a, b) => b.percent - a.percent)
                                .slice(0, 3)
                                .map(v => {
                                    const isGrowing = v.trend > 0.5;
                                    const isShrinking = v.trend < -0.5;
                                    return (
                                        <div key={v.key} style={{
                                            padding: '20px',
                                            background: `linear-gradient(135deg, ${v.color}10 0%, ${v.color}18 100%)`,
                                            borderRadius: '12px',
                                            borderBottom: `3px solid ${v.color}`,
                                        }}>
                                            <div style={{ fontSize: '32px', fontWeight: 700, color: v.color }}>
                                                {v.percent.toFixed(0)}%
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#555', fontWeight: 500, marginTop: '4px' }}>
                                                {v.name}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#888', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {isGrowing && (
                                                    <>
                                                        <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 1L9 6H1L5 1Z" fill="#10B981"/></svg>
                                                        <span style={{ color: '#10B981' }}>+{v.trend.toFixed(1)} over period</span>
                                                    </>
                                                )}
                                                {isShrinking && (
                                                    <>
                                                        <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 9L1 4H9L5 9Z" fill="#EF4444"/></svg>
                                                        <span style={{ color: '#EF4444' }}>{v.trend.toFixed(1)} over period</span>
                                                    </>
                                                )}
                                                {!isGrowing && !isShrinking && (
                                                    <span>Stable over period</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            }
                        </div>
                    )}
                </>
            )}

            {/* Empty state after apply */}
            {!isLoading && !error && !hasData && appliedProduct && (
                <div style={{
                    textAlign: 'center', padding: '80px 20px', color: '#94A3B8',
                    fontSize: '14px', backgroundColor: '#FAFBFD', borderRadius: '12px',
                    border: '1px dashed #E2E8F0',
                }}>
                    No demographic data available for the selected filters.
                </div>
            )}

            {/* NPS by Demographic Segment */}
            {!isLoading && !error && hasData && viewMode === 'nps' && (
                <div style={{ animation: 'fadeInUp 0.4s ease both 120ms' }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: '12px',
                    }}>
                        <span style={{ fontSize: '13px', color: '#64748B' }}>
                            Net Promoter Score segmented by {formatTypeName(appliedType)} classification
                        </span>
                        <button
                            onClick={() => setShowNpsConfidence(!showNpsConfidence)}
                            style={{
                                padding: '5px 14px',
                                backgroundColor: showNpsConfidence ? '#6366F1' : '#f5f5f5',
                                color: showNpsConfidence ? '#fff' : '#555',
                                border: showNpsConfidence ? '1px solid #6366F1' : '1px solid #ddd',
                                borderRadius: '20px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 500,
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {showNpsConfidence ? 'Hide Intervals' : 'Show Intervals'}
                        </button>
                    </div>

                    {npsLoading ? (
                        <div style={{
                            height: 480, display: 'flex', justifyContent: 'center', alignItems: 'center',
                            backgroundColor: '#FAFBFD', borderRadius: '12px', border: '1px solid #F1F5F9',
                        }}>
                            <CircularProgress size={28} sx={{ color: '#6366F1' }} />
                            <span style={{ marginLeft: '12px', fontSize: '13px', color: '#64748B' }}>
                                Loading NPS by segment...
                            </span>
                        </div>
                    ) : npsChartData ? (
                        <div style={{
                            height: 480,
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            border: '1px solid #F1F5F9',
                            padding: '16px',
                            boxSizing: 'border-box',
                            cursor: appliedProduct !== 'all' ? 'pointer' : 'default',
                        }}>
                            <NPSChart
                                chartData={npsChartData}
                                showConfidenceIntervals={showNpsConfidence}
                                onPointClick={handleNpsPointClick}
                            />
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center', padding: '40px 20px', color: '#94A3B8',
                            fontSize: '13px', backgroundColor: '#FAFBFD', borderRadius: '12px',
                            border: '1px dashed #E2E8F0',
                        }}>
                            No NPS data available for these demographic segments.
                        </div>
                    )}
                </div>
            )}

            {/* Posts Display */}
            {(postsLoading || selectedPeriod) && (
                <div style={{ marginTop: '24px', animation: 'fadeInUp 0.3s ease both' }}>
                    {/* Period header — always visible once clicked */}
                    {selectedPeriod && (
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '12px 16px', backgroundColor: '#F8FAFC', borderRadius: '8px 8px 0 0',
                            border: '1px solid #E2E8F0', borderBottom: postsReferenced.length > 0 ? 'none' : undefined,
                        }}>
                            <div>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1E293B' }}>
                                    {formatTypeName(appliedType)} Posts
                                </span>
                                <span style={{ fontSize: '13px', color: '#64748B', marginLeft: '8px' }}>
                                    {selectedPeriod}
                                </span>
                            </div>
                            <button
                                onClick={handleClosePosts}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    fontSize: '18px', color: '#94A3B8', padding: '4px 8px',
                                    borderRadius: '4px', lineHeight: 1,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E2E8F0'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                                &times;
                            </button>
                        </div>
                    )}

                    {/* Loading spinner */}
                    {postsLoading && (
                        <div style={{
                            display: 'flex', justifyContent: 'center', alignItems: 'center',
                            padding: '40px', backgroundColor: '#FAFBFD',
                            border: '1px solid #E2E8F0', borderTop: selectedPeriod ? 'none' : undefined,
                            borderRadius: selectedPeriod ? '0 0 8px 8px' : '8px',
                        }}>
                            <CircularProgress size={28} sx={{ color: '#6366F1' }} />
                            <span style={{ marginLeft: '12px', fontSize: '13px', color: '#64748B' }}>
                                Loading posts...
                            </span>
                        </div>
                    )}

                    {/* Error message */}
                    {!postsLoading && postsError && postsReferenced.length === 0 && (
                        <div style={{
                            padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '13px',
                            backgroundColor: '#FAFBFD', border: '1px solid #E2E8F0', borderTop: 'none',
                            borderRadius: '0 0 8px 8px',
                        }}>
                            {postsError}
                        </div>
                    )}

                    {/* Posts list */}
                    {!postsLoading && postsReferenced.length > 0 && (
                        <MotivationPostsDisplay
                            posts={postsReferenced}
                            onClose={handleClosePosts}
                            motivationType={formatTypeName(appliedType)}
                            subtitle={selectedPeriod}
                            autoScroll={true}
                            hideHeader={true}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default DemographicEvaluator;
